import type { Corner, ShotKind } from "../types"
import type { LiveViewJudgement } from "../brain/combine"
import { focusIssue, lightIssue, type Area, type FrameMetrics } from "./frame-metrics"
import type { Box, Fit } from "./framing"
import { glareStuck, viewStuck, wantsManualHint, type LockState } from "./lock-machine"

// The ONE live coaching line under the shot title. Priority: light, then a
// reflection, then (smart camera, whole car) the corner and the fit, then
// steadiness, then focus. At most 8 words, plain, second person, no dashes,
// and it blames nobody. Pure; unit-tested.

export type CoachTone = "neutral" | "good" | "warn"

export interface Coach {
  line: string
  tone: CoachTone
  // The shutter rim turns green: a tap now gives a good photo.
  ready: boolean
}

export interface CoachInput {
  t: number
  kind: ShotKind
  // The corner asked for (whole-car photos).
  corner: Corner | null
  // The car detector is running (smart camera).
  smart: boolean
  metrics: FrameMetrics | null
  // Sharpest value seen for this shot (relative focus check).
  bestSharpness: number
  still: boolean
  fit: Fit | null
  // The car-view brain's live answer (whole-car photos), null: none yet.
  view: LiveViewJudgement | null
  lock: LockState | null
  torch: boolean
  // A reflection blows out part of what the photo is for (see glareArea).
  glare: boolean
  // Too close to fit the whole car since this time (a streak that a
  // moment of not seeing the car doesn't break), how far to back up
  // (steady: stepBackLevel), and what else could help.
  tooClose?: {
    since: number
    level: StepBackLevel
    // Backing up isn't helping (the car hasn't got smaller lately).
    stuck: boolean
    // The wide lens's button label ("0.5×"), when it's there to tap.
    widen: string | null
    canTurn: boolean
  } | null
}

// Too close this long, and backing up isn't helping: offer the wide lens,
// else turning sideways.
export const ROOM_HINT_MS = 4000

export function wideLensLine(label: string): string {
  return `Can't step back? Tap ${label}.`
}


export const COACH_LINE = {
  dark: "Too dark. Find more light.",
  darkTorch: "Too dark. Tap the light.",
  glare: "Step to the side to lose the glare.",
  glareTap: "Can't lose the glare? Tap the button.",
  holdStill: "Hold still.",
  focus: "Move back a little so it can focus.",
  stepBack: "Step back a little.",
  stepBackOne: "Take a step back.",
  stepBackFew: "Take a few steps back.",
  // Still too close after a while: no room to back up (a narrow street).
  wideLens: "Can't step back? Tap 0.5×.",
  turnSideways: "Turn your phone sideways.",
  comeCloser: "Come a bit closer.",
  aimLeft: "Aim a little left.",
  aimRight: "Aim a little right.",
  aimUp: "Aim a little higher.",
  aimDown: "Aim a little lower.",
  stepCloser: "Take a step closer.",
  findCar: "Point the camera at the whole car.",
  manual: "Whole car in frame? Tap the button.",
  holdIt: "Hold it right there.",
  gotIt: "Got it.",
  fitWide: "Fit the whole car, then tap the button.",
  readyCloser: "Looks good. Tap the button.",
  readyClose: "Looks sharp. Tap the button.",
} as const

// How far to back up: a little, a step, a few steps.
export type StepBackLevel = 0 | 1 | 2
const STEP_BACK_LINES = [COACH_LINE.stepBack, COACH_LINE.stepBackOne, COACH_LINE.stepBackFew] as const
// Where one becomes the next (times too big).
const STEP_BACK_AT: readonly number[] = [1.2, 1.6]

export function stepBackLine(over: number | undefined): string {
  return STEP_BACK_LINES[stepBackLevel(null, over ?? 1)]
}

// Steady while the car hovers near a threshold: up only once clearly past
// it, down only once clearly under (the line doesn't flicker).
export function stepBackLevel(prev: StepBackLevel | null, over: number): StepBackLevel {
  if (prev === null) return over < STEP_BACK_AT[0]! ? 0 : over < STEP_BACK_AT[1]! ? 1 : 2
  let level: StepBackLevel = prev
  while (level < 2 && over >= STEP_BACK_AT[level]! + 0.05) level = (level + 1) as StepBackLevel
  while (level > 0 && over < STEP_BACK_AT[level - 1]! - 0.1) level = (level - 1) as StepBackLevel
  return level
}

const END_NAME = { front: "front", rear: "back" } as const
const SIDE_NAME = { left: "driver's", right: "passenger" } as const
const endOf = (c: Corner) => (c === "FL" || c === "FR" ? "front" : "rear")
const sideOf = (c: Corner) => (c === "FL" || c === "RL" ? "left" : "right")

// What to do when the brain sees another corner, or the car square-on.
export function viewLine(asked: Corner, view: LiveViewJudgement): string | null {
  if (view.kind === "angle") {
    return view.toward === "side"
      ? `Take a step toward the ${SIDE_NAME[sideOf(asked)]} side.`
      : `Take a step toward the ${END_NAME[endOf(asked)]}.`
  }
  if (view.kind === "wrong") {
    const end = endOf(asked)
    if (view.seen.end && view.seen.end !== end) return `That's the ${END_NAME[view.seen.end]}. Walk to the ${END_NAME[end]}.`
    const side = sideOf(asked)
    if (view.seen.side && view.seen.side !== side) return `Walk round to the ${SIDE_NAME[side]} side.`
  }
  return null
}

const FIT_LINE: Record<Exclude<Fit["issue"], null | "no-car">, string> = {
  "step-back": COACH_LINE.stepBack,
  "come-closer": COACH_LINE.comeCloser,
  "aim-left": COACH_LINE.aimLeft,
  "aim-right": COACH_LINE.aimRight,
  "aim-up": COACH_LINE.aimUp,
  "aim-down": COACH_LINE.aimDown,
}

export function coachFor(c: CoachInput): Coach {
  if (c.metrics && lightIssue(c.metrics)) {
    return { line: c.torch ? COACH_LINE.darkTorch : COACH_LINE.dark, tone: "warn", ready: false }
  }
  const blurry = !!c.metrics && !!focusIssue(c.metrics, c.bestSharpness)
  if (c.lock?.phase === "snapped") return { line: COACH_LINE.gotIt, tone: "good", ready: true }
  if (c.glare) {
    return c.lock && glareStuck(c.lock, c.t)
      ? { line: COACH_LINE.glareTap, tone: "warn", ready: false }
      : { line: COACH_LINE.glare, tone: "warn", ready: false }
  }

  if (c.kind === "wide" && c.smart && c.lock) {
    if (c.lock.phase === "locked") {
      if (!c.still) return { line: COACH_LINE.holdStill, tone: "neutral", ready: false }
      if (blurry) return { line: COACH_LINE.focus, tone: "warn", ready: false }
      return { line: COACH_LINE.holdIt, tone: "good", ready: true }
    }
    // Never stuck: the brain has refused a car that fits for a while (it can
    // be wrong about an unusual car), so the person decides; the verdict after
    // the photo still says if it's the wrong corner.
    if (viewStuck(c.lock, c.t)) return { line: COACH_LINE.manual, tone: "neutral", ready: false }
    // The car fits; the brain says it's another corner, or square-on.
    const wrongView = c.corner && c.view ? viewLine(c.corner, c.view) : null
    if (wrongView) return { line: wrongView, tone: "neutral", ready: false }
    if (c.lock.phase === "verifying") return { line: COACH_LINE.holdIt, tone: "neutral", ready: false }
    const issue = c.fit?.issue
    // Too close (also for a moment when it's too close to be found at all):
    // how far to back up; after a while, what else would help.
    if (c.tooClose && (issue === "step-back" || issue === "no-car")) {
      if (c.tooClose.stuck && c.t - c.tooClose.since >= ROOM_HINT_MS) {
        if (c.tooClose.widen) return { line: wideLensLine(c.tooClose.widen), tone: "neutral", ready: false }
        if (c.tooClose.canTurn) return { line: COACH_LINE.turnSideways, tone: "neutral", ready: false }
      }
      return { line: STEP_BACK_LINES[c.tooClose.level], tone: "neutral", ready: false }
    }
    if (issue === "step-back") return { line: stepBackLine(c.fit?.over), tone: "neutral", ready: false }
    if (issue && issue !== "no-car") return { line: FIT_LINE[issue], tone: "neutral", ready: false }
    // Can't find the car for a while (night, an unusual vehicle): stop
    // waiting for the detector and hand the shutter to the person.
    return wantsManualHint(c.lock, c.t)
      ? { line: COACH_LINE.manual, tone: "neutral", ready: false }
      : { line: COACH_LINE.findCar, tone: "neutral", ready: false }
  }

  if (c.kind === "closer" && c.smart && c.fit?.issue === "come-closer") {
    return { line: COACH_LINE.stepCloser, tone: "neutral", ready: false }
  }
  if (!c.still) return { line: COACH_LINE.holdStill, tone: "neutral", ready: false }
  if (blurry) return { line: COACH_LINE.focus, tone: "warn", ready: false }

  switch (c.kind) {
    case "wide":
      return { line: COACH_LINE.fitWide, tone: "neutral", ready: true }
    case "closer":
      return { line: COACH_LINE.readyCloser, tone: "good", ready: true }
    default:
      return { line: COACH_LINE.readyClose, tone: "good", ready: true }
  }
}

// Where a reflection matters. Whole car and closer: the body panels (the
// lower part of the car), never the windows and sky, which always shine;
// a whole car only once it's found. Close-ups: the middle, where the damage
// is. The VIN sticker: nowhere (a white label is bright, not a reflection).
export function glareArea(kind: ShotKind, box: Box | null): Area | null {
  switch (kind) {
    case "wide":
      return box ? bodyOf(box) : null
    case "closer":
      return box ? bodyOf(box) : MIDDLE
    case "closeup":
      return MIDDLE
    case "sticker":
      return null
  }
}

const bodyOf = (box: Box): Area => ({ x: box.x, y: box.y + box.h * 0.45, w: box.w, h: box.h * 0.55 })

const MIDDLE: Area = { x: 0.2, y: 0.2, w: 0.6, h: 0.6 }
